import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, MessageSquare, Users, Settings, Bot, Crown, Sparkles, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useLocation, Link } from "react-router-dom";

// Discord OAuth2 Configuration
const DISCORD_CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_DISCORD_REDIRECT_URI;
const DISCORD_API_ENDPOINT = "https://discord.com/api/v10";
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY; 

// Bot Invite URL
const BOT_INVITE_URL = `https://discord.com/oauth2/authorize?client_id=1324844843670835240`;

// Liste der Server-IDs, in denen der Bot Mitglied ist
// Diese Liste sollte regelmäßig aktualisiert werden
const BOT_GUILDS = [
  "1357782920873640076", // Fembot Support Server
  "1358094676598718696"  // Testing Server
];

// Discord Permissions
const ADMINISTRATOR = 0x8n;

interface DiscordUser {
  id: string;
  username: string;
  avatar: string;
  discriminator: string;
  global_name?: string;
}

interface GuildData {
  id: string;
  name: string;
  icon: string;
  owner: boolean;
  permissions: string;
  features: string[];
  premium_tier?: number;
  bot_joined?: boolean;
}

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<DiscordUser | null>(null);
  const [guilds, setGuilds] = useState<GuildData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Prüfe auf Access Token im URL-Fragment
        const fragment = new URLSearchParams(window.location.hash.slice(1));
        const accessToken = fragment.get("access_token");
        const storedToken = localStorage.getItem("discord_token");
        
        if (accessToken) {
          // Neuer Login über OAuth
          localStorage.setItem("discord_token", accessToken);
          await fetchUserData(accessToken);
          // Token aus URL entfernen
          window.history.replaceState({}, document.title, "/dashboard");
        } else if (storedToken) {
          // Versuche gespeicherten Token zu nutzen
          const isValid = await validateToken(storedToken);
          if (isValid) {
            await fetchUserData(storedToken);
          } else {
            handleLogout();
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        handleLogout();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [location]);

  const validateToken = async (token: string) => {
    try {
      const response = await fetch(`${DISCORD_API_ENDPOINT}/users/@me`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.ok;
    } catch {
      return false;
    }
  };

  const fetchUserData = async (token: string) => {
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };

      // Benutzerinformationen abrufen
      const userResponse = await fetch(`${DISCORD_API_ENDPOINT}/users/@me`, {
        headers
      });
      
      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }
      
      const userData = await userResponse.json();
      setUser(userData);
      localStorage.setItem('discord_user', JSON.stringify(userData));

      // Server-Liste abrufen
      const guildsResponse = await fetch(`${DISCORD_API_ENDPOINT}/users/@me/guilds`, {
        headers
      });
      
      if (!guildsResponse.ok) {
        throw new Error('Failed to fetch guilds data');
      }
      
      const guildsData = await guildsResponse.json();
      
      // Filtere Server basierend auf Berechtigungen und Bot-Mitgliedschaft
      const adminGuilds = guildsData.filter((guild: GuildData) => {
        const permissions = BigInt(guild.permissions);
        // Speichere Servernamen im localStorage
        localStorage.setItem(`server_${guild.id}_name`, guild.name);
        return guild.owner || (permissions & ADMINISTRATOR) === ADMINISTRATOR;
      }).map((guild: GuildData) => ({
        ...guild,
        bot_joined: BOT_GUILDS.includes(guild.id)
      }));
      
      setGuilds(adminGuilds);
      
      toast({
        title: "Successfully logged in",
        description: "Welcome back >w<",
      });
    } catch (error) {
      console.error("Data fetching error:", error);
      toast({
        title: "Error",
        description: "Could not fetch Discord data. Please try again.",
        variant: "destructive"
      });
      handleLogout();
    }
  };

  // Turnstile Script dynamisch laden, wenn User nicht eingeloggt ist
  useEffect(() => {
    if (user !== null) return;
    if (!TURNSTILE_SITE_KEY) {
      setTurnstileError("Turnstile Site Key fehlt. Bitte Umgebungsvariable VITE_TURNSTILE_SITE_KEY setzen.");
      return;
    }
    if (document.getElementById("cf-turnstile-script")) return;
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.onload = () => setTurnstileReady(true);
    script.onerror = () => setTurnstileError("Turnstile script could not be loaded.");
    document.body.appendChild(script);
    return () => {
      script.remove();
    };
  }, [user]);

  // Turnstile Widget initialisieren, wenn Script geladen und User nicht eingeloggt
  useEffect(() => {
    if (!turnstileReady || user !== null) return;
    if (!TURNSTILE_SITE_KEY) return;
    const widgetDiv = document.getElementById("turnstile-widget");
    if (!widgetDiv) return;
    widgetDiv.innerHTML = ""; // Widget-Container leeren, falls schon ein Widget existiert
    try {
      // @ts-ignore
      if (window.turnstile && typeof window.turnstile.render === "function") {
        // @ts-ignore
        window.turnstile.render(widgetDiv, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileError(null);
          },
          "error-callback": () => {
            setTurnstileToken(null);
            setTurnstileError("Turnstile was unable to be loaded. Please try again.");
            console.error("Turnstile error-callback triggered");
          },
          "expired-callback": () => {
            setTurnstileToken(null);
            setTurnstileError("Turnstile expired, retrying...");
            console.warn("Turnstile expired-callback triggered");
          },
          theme: document.documentElement.classList.contains("dark") ? "dark" : "light"
        });
      } else {
        setTurnstileError("Turnstilecould not be initialized (window.turnstile missing).");
        console.error("window.turnstile is not available!");
      }
    } catch (err) {
      setTurnstileError("Error initiating turnstyle: " + (err instanceof Error ? err.message : String(err)));
      console.error("Error initiating turnstyle:", err);
    }
  }, [turnstileReady, user]);

  const handleLogin = () => {
    if (!turnstileToken) {
      setTurnstileError("Please solve the captcha.");
      return;
    }
    if (!DISCORD_CLIENT_ID) {
      toast({
        title: "Configuration Error",
        description: "Discord Client ID is not configured.",
        variant: "destructive"
      });
      return;
    }
    // Optional: Turnstile-Token an Backend senden und validieren
    // ...
    window.location.href = `https://discord.com/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI || '')}&response_type=token&scope=identify%20guilds`;
  };

  const handleLogout = () => {
    localStorage.removeItem("discord_token");
    localStorage.removeItem("discord_user");
    setUser(null);
    setGuilds([]);
    toast({
      title: "Logged out",
      description: "See you soon! >w< ",
    });
  };

  const handleServerAction = (guildId: string, action: string) => {
    navigate(`/server/${guildId}?tab=${action}`);
  };

  const handleServerClick = (guildId: string) => {
    navigate(`/server/${guildId}?tab=overview`);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[calc(100vh-6rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fembot-pink mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[calc(100vh-6rem)] bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
          <div className="container-custom max-w-lg text-center px-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 dark:text-white">
                Bot <span className="text-fembot-pink">Dashboard</span>
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                Please log in with Discord to manage your servers
              </p>
              {/* Turnstile Widget */}
              {TURNSTILE_SITE_KEY ? (
                <div
                  id="turnstile-widget"
                  className="flex justify-center items-center mb-4"
                  style={{ minHeight: 70, minWidth: 320 }}
                ></div>
              ) : (
                <div className="text-red-500 text-sm mb-4">Turnstile Site Key missing.</div>
              )}
              {turnstileError && (
                <div className="text-red-500 text-sm mb-2">{turnstileError}</div>
              )}
              <Button
                onClick={handleLogin}
                className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-8 py-6 text-lg flex items-center gap-2 mx-auto mb-4"
                style={{ minHeight: 70, minWidth: 320 }}
                disabled={!turnstileToken || !TURNSTILE_SITE_KEY}
              >
                {/*DC logo*/}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-6 h-6"
                  aria-hidden="true"
                >
                  <path d="M20.317 4.369A19.791 19.791 0 0 0 16.885 3.1a.077.077 0 0 0-.082.038c-.357.63-.755 1.453-1.037 2.104a18.524 18.524 0 0 0-5.462 0 12.51 12.51 0 0 0-1.05-2.104.077.077 0 0 0-.082-.038A19.736 19.736 0 0 0 3.677 4.369a.069.069 0 0 0-.032.027C.533 9.09-.32 13.579.099 18.021a.082.082 0 0 0 .031.056c2.128 1.565 4.195 2.51 6.222 3.13a.077.077 0 0 0 .084-.027c.48-.66.91-1.356 1.273-2.084a.076.076 0 0 0-.041-.104c-.676-.256-1.32-.568-1.934-.936a.077.077 0 0 1-.008-.127c.13-.098.26-.2.384-.304a.074.074 0 0 1 .077-.01c4.06 1.855 8.448 1.855 12.47 0a.075.075 0 0 1 .078.009c.124.104.254.206.384.304a.077.077 0 0 1-.006.127 12.298 12.298 0 0 1-1.936.936.076.076 0 0 0-.04.105c.36.728.792 1.423 1.272 2.083a.076.076 0 0 0 .084.028c2.027-.62 4.094-1.565 6.222-3.13a.077.077 0 0 0 .031-.055c.5-5.177-.838-9.637-3.548-13.625a.061.061 0 0 0-.03-.028ZM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.094 2.157 2.418 0 1.334-.955 2.419-2.157 2.419Zm7.974 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.418 2.157-2.418 1.21 0 2.175 1.094 2.157 2.418 0 1.334-.947 2.419-2.157 2.419Z" />
                </svg>
                Login with Discord
              </Button>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                By logging in, you agree to our{" "}
                <Link to="/terms" className="text-fembot-pink hover:underline">Terms of Service</Link>
                {" "}and{" "}
                <Link to="/privacy" className="text-fembot-pink hover:underline">Privacy Policy</Link>
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900 py-24">
        <div className="container-custom">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl mb-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="text-center md:text-left">
                <h1 className="text-3xl font-bold dark:text-white mb-2">
                  Haiiii there <span className="text-fembot-pink">{user.username}</span>!
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Manage your Discord servers and bot settings
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="border-fembot-pink text-fembot-pink hover:bg-fembot-pink hover:text-white transition-colors"
              >
                Logout
              </Button>
            </div>
          </div>

          {/* Premium Banner */}
          <div className="bg-gradient-to-r from-fembot-pink to-fembot-dark-pink rounded-2xl p-8 shadow-xl mb-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
              <Sparkles className="w-full h-full" />
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 relative z-10">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Supercharge Your Servers!
                </h2>
                <p className="text-pink-100">
                  Unlock advanced features, AI capabilities, and enhanced moderation tools with Fembot Premium
                </p>
              </div>
              <Link to="/premium">
                <Button 
                  size="lg"
                  className="bg-white hover:bg-gray-100 text-fembot-pink border-2 border-transparent hover:border-white hover:text-fembot-dark-pink transition-all duration-300 font-semibold flex items-center gap-2"
                >
                  <Crown className="w-5 h-5" />
                  Upgrade to Premium
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {guilds.map((guild) => (
              <Card 
                key={guild.id} 
                className="transform hover:scale-105 transition-all duration-300 border-0 shadow-xl bg-white dark:bg-gray-800 cursor-pointer"
                onClick={() => handleServerClick(guild.id)}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-fembot-pink to-fembot-dark-pink flex items-center justify-center">
                        {guild.icon ? (
                          <img 
                            src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`} 
                            alt={guild.name}
                            className="w-full h-full rounded-full"
                          />
                        ) : (
                          <Users className="w-6 h-6 text-white" />
                        )}
                      </div>
                      <div>
                        <CardTitle className="text-xl dark:text-white">
                          {guild.name}
                        </CardTitle>
                        <CardDescription>
                          {guild.owner ? "Server Owner" : "Administrator"}
                          {guild.premium_tier > 0 && (
                            <span className="ml-2 inline-flex items-center text-amber-500">
                              <Crown className="w-4 h-4 mr-1" />
                              Level {guild.premium_tier}
                            </span>
                          )}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {guild.bot_joined ? (
                    <div className="grid grid-cols-2 gap-4">
                      <Button 
                        variant="outline" 
                        className="w-full hover:bg-fembot-pink hover:text-white border-fembot-pink text-fembot-pink transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServerAction(guild.id, "moderation");
                        }}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Moderation
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full hover:bg-fembot-pink hover:text-white border-fembot-pink text-fembot-pink transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServerAction(guild.id, "ai");
                        }}
                      >
                        <Bot className="w-4 h-4 mr-2" />
                        AI Features
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full hover:bg-fembot-pink hover:text-white border-fembot-pink text-fembot-pink transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServerAction(guild.id, "messages");
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Messages
                      </Button>
                      <Button 
                        variant="outline"
                        className="w-full hover:bg-fembot-pink hover:text-white border-fembot-pink text-fembot-pink transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleServerAction(guild.id, "settings");
                        }}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Fembot is not a member of this server yet
                      </p>
                      <Button 
                        className="bg-fembot-pink hover:bg-fembot-dark-pink text-white w-full"
                        onClick={() => window.location.href = '/info'}
                       /* onClick={(e) => {
                          e.stopPropagation();
                          window.open(`${BOT_INVITE_URL}&guild_id=${guild.id}`, '_blank');
                        }} */
                        >
                        <Bot className="w-4 h-4 mr-2" />
                        Add Bot to Server
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;